
import { Component, Vue, Watch } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import Themeable from 'vuetify/lib/mixins/themeable';
import WebsiteGroupRepositoryInterface, { WebsiteGroupItem } from '../../../services/WebsiteGroup/interfaces';
import WebsiteRepositoryInterface, { Website } from '@/services/Website/interfaces';
import { UserBaseInfoInterface, UserInterface, UserRepoInterface } from '@/services/Users/Interfaces';
import UserPicker from '@/components/user/UserPicker.vue';
import WebsitePicker from '@/components/website/WebsitePicker.vue';
import { PlanRepositoryInterface } from '@/services/Plan/interfaces';
import { ServerRepositoryInterface } from '@/services/Server/Interfaces';
import VComponent from '@/components/base/VComponent.vue';
import { UsageRepositoryInterface } from "@/services/Usage/interfaces";
import PageHeader from '@/components/layout/PageHeader.vue';
import {
  LIST_FOOTER_PROPS,
  buildListPaginationQuery,
  defaultListOptions,
  effectivePageSize,
  listPaginationQueryEquals,
  parseListQuery,
} from '@/utils/listPagination';

@Component({
  components: {
    PageHeader,
    VComponent,
    WebsitePicker,
    UserPicker,
  },
  mixins: [Themeable],
  inject: [
    'websiteGroupRepository',
    'websiteRepository',
    'userRepository',
    'planRepository',
    'serverRepository',
    'usageRepository',
  ],
})
export default class UsageList extends Vue {
  public loading: boolean = true;

  public userSearchLoading = true;

  public usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [];

  public options = defaultListOptions();

  public footerProps = LIST_FOOTER_PROPS;

  public totalGroupsCount = 0;
  public ownerFilter: string | null = null;

  private ignoreRouteSync = false;
  private applyingRouteQuery = false;

  private websiteGroupRepository!: WebsiteGroupRepositoryInterface;
  private websiteRepository!: WebsiteRepositoryInterface;
  private userRepository!: UserRepoInterface;
  private planRepository!: PlanRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private usageRepository!: UsageRepositoryInterface;

  public headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Plan', value: 'plan' },
    { text: 'Main Website', value: 'mainWebsite' },
    { text: 'Server', value: 'server' },
    { text: 'Usage', value: 'usage' },
    { text: 'Actions', value: 'actions' },
  ];

  public groups: WebsiteGroupItem[] = [];
  public websites: Website[] = [];
  public users: UserBaseInfoInterface[] = [];
  public plansMap: { [key: number]: string } = {};
  public usageUsedMap: { [key: number]: number } = {};
  public usageQuotaMap: { [key: number]: number } = {};

  public async created(): Promise<void> {
    this.applyRouteQuery();
    await this.loadPlans();
    await this.loadUsersForSelect();
    await this.initialize();
  }

  public applyRouteQuery(): void {
    this.applyingRouteQuery = true;
    try {
      this.options = parseListQuery(this.$route.query);
      this.$nextTick(() => {
        this.applyingRouteQuery = false;
      });
    } catch (err) {
      this.applyingRouteQuery = false;
      throw err;
    }
  }

  public async syncRouteQuery(): Promise<void> {
    if (this.ignoreRouteSync || this.applyingRouteQuery) {
      return;
    }

    const query = buildListPaginationQuery(this.options);
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.ignoreRouteSync = true;
    try {
      await this.$router.replace({ name: 'usage_list', query });
    } catch (err) {
      // Ignore NavigationDuplicated.
    } finally {
      this.$nextTick(() => {
        this.ignoreRouteSync = false;
      });
    }
  }

  public async initialize(): Promise<void> {
    try {
      this.loading = true;

      await this.loadWebsiteGroups();
      await this.loadUsage();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();

      this.loading = false;
    } catch (err) {
      this.$errorDialog.show(`An issue ocurred while retrieving website groups. ${err}`);
    }
  }

  public async onPageChange(): Promise<void> {
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsiteGroups();
      await this.loadUsage();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  public async onItemsPerPageChange(): Promise<void> {
    this.options.page = 1;
    this.loading = true;
    try {
      await this.syncRouteQuery();
      await this.loadWebsiteGroups();
      await this.loadUsage();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  @Watch('$route')
  public async onRouteChange(): Promise<void> {
    if (this.ignoreRouteSync) {
      return;
    }
    if (this.$route.name !== 'usage_list') {
      return;
    }
    if (listPaginationQueryEquals(this.$route.query, this.options)) {
      return;
    }

    this.applyRouteQuery();
    this.loading = true;
    try {
      await this.loadWebsiteGroups();
      await this.loadUsage();
      await this.loadWebsites();
      await this.loadUsers();
      await this.loadServers();
    } catch (err) {
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  public async loadWebsiteGroups(clearPage?: boolean): Promise<void> {
    try {
      this.loading = true;
      if (clearPage) {
        this.options.page = 1;
      }

      const groupsData = await this.websiteGroupRepository.searchGroups(
        this.ownerFilter,
        this.options.page,
        effectivePageSize(this.options.itemsPerPage),
      );

      if (!groupsData?.groups) {
        this.groups = [];
        this.totalGroupsCount = 0;
        this.loading = false;

        return;
      }

      this.groups = groupsData.groups;
      this.totalGroupsCount = groupsData.count;

      if (clearPage) {
        await this.syncRouteQuery();
        await this.loadWebsites();
        await this.loadUsers();
        await this.loadServers();
        await this.loadUsage();
      }

      this.loading = false;
    } catch (err) {
      this.groups = [];
      this.totalGroupsCount = 0;
      this.$errorDialog.show(`An issue occurred while retrieving website groups. ${err}`);
    } finally {
      this.loading = false;
    }
  }

  private async loadWebsites(): Promise<void> {
    const ids = this.groups.map((group) => group.mainWebsite);
    if (!ids.length) {
      return;
    }

    this.websites = await this.websiteRepository.getWebsitesByIds(ids);
  }

  private async loadUsers(): Promise<void> {
    const ids = this.groups.map((group) => group.owner).filter((owner) => owner.length === 24);
    if (!ids.length) {
      return;
    }

    this.users = await this.userRepository.getUsersByIds(ids);
  }

  private async loadUsersForSelect(): Promise<void> {
    try {
      const usersResponse = await this.userRepository.getAllUsers(0);
      const usersForSelect: (UserInterface | { header: string } | { divider: boolean })[] = [{ header: 'Fenix' }];
      usersForSelect.push(...usersResponse[0]);

      const hbOwners = await this.websiteGroupRepository.getHBOwners();
      usersForSelect.push({ divider: true }, { header: 'Hostbill' });

      usersForSelect.push(
        ...hbOwners.map((owner) => ({ id: owner, username: `HB ${owner}` } as unknown as UserInterface)),
      );

      this.usersForSelect = usersForSelect;

      this.userSearchLoading = false;
    } catch (err) {
      this.$errorDialog.show(err.message);
    }
  }


  public async loadPlans(): Promise<void> {
    const plans = await this.planRepository.getAll();
    plans.forEach((plan) => {
      this.plansMap[plan.id] = plan.name;
    });
  }

  public async loadServers(): Promise<void> {
    const serversIds: string[] = [];
    this.websites.forEach((website) => {
      if (!serversIds.includes(website.originServer)) {
        serversIds.push(website.originServer);
      }
    });

    if (!serversIds.length) {
      return;
    }

    const servers = await this.serverRepository.getServersByIds(serversIds);
    const serversMap: { [key: string]: string } = {};
    servers.forEach((server) => {
      serversMap[server._id] = server.hostname;
    });


    this.websites.forEach((website) => {
      const groupIndex = this.groups.findIndex((g) => g.mainWebsite === website.id);
      if (groupIndex === -1) {
        return;
      }

      this.$set(this.groups[groupIndex], 'server', serversMap[website.originServer]);
    });
  }

  public async loadUsage(): Promise<void> {
    await Promise.all(this.groups.map(x => this.usageRepository.getDiskUsageByGroup(x.id).then(y => {
      this.usageUsedMap[x.id] = y.quotaSnapshot.blockUsed;
      this.usageQuotaMap[x.id] = y.quotaSnapshot.blockHard;
    }).catch(() => {})));
  }

  public getUsedBytes(groupId: number): number {
    return (this.usageUsedMap[groupId] ?? 0) / 1024;
  }

  public getQuotaBytes(groupId: number): number {
    return (this.usageQuotaMap[groupId] ?? 0) / 1024;
  }

  private viewUsage(groupId: number): void {
    this.$router.push({ name: 'usage_details', params: { groupId } });
  }

  public formatBytes(kib: number | null | undefined): string {
    if (kib === null || kib === undefined) {
      return '0';
    }

    // KiB to MB
    const mb = kib / 976.6;

    // If less than 1 GB -show in MB
    if (mb < 1000) {
      return `${Math.round(mb)} MB`;
    }

    // 1 GB or more - show in GB with 2 decimal
    const gb = mb / 1000;
    return `${gb.toFixed(2)} GB`;
  }

  private getWebsiteDomain(websiteId: number): string {
    const website = this.websites.find((w) => w.id === websiteId);
    if (!website) {
      return '';
    }

    return website.domain;
  }

  private getUserEmail(userId: string): string {
    const user = this.users.find((w) => w.id === userId);
    if (!user) {
      return '';
    }

    return user.email;
  }
}
